import { createStore } from 'zustand/vanilla';
import { useStore } from 'zustand';
import { AssistantChatLogRating } from '@mews-commander/data-access';
import { AssistantChatMessage } from '../chat/components/AssistantChatMessageContainer';
import { AssistantChatMessageSearchResult } from '../chat/query/useAssistantChatSearchResultQuery';
import { AssistantChatMenuType } from '../chat/components/AssistantChatMenuType';

export interface AssistantChatMessageResult {
    assistantChatMessage: AssistantChatMessage;
    assistantChatMessageSearchResult?: AssistantChatMessageSearchResult;
}

interface AssistantChatDataProps {
    chatMessageResult: Record<string, Record<string, AssistantChatMessageResult>>;
    parentChatID?: string;
    chatMessageRating?: Record<string, AssistantChatLogRating>;
    selectedArchiveChatId?: string;
    selectedMenuType?: AssistantChatMenuType;
}

export const assistantChatMessageInitialState: AssistantChatDataProps = {
    chatMessageResult: {},
    parentChatID: undefined,
    chatMessageRating: {},
    selectedArchiveChatId: undefined,
    selectedMenuType: AssistantChatMenuType.NewChat,
};

interface AssistantChatDataStore extends AssistantChatDataProps {
    setAssistantChatDataStore: (state: AssistantChatDataProps) => void;
    setParentChatId: (chatID: string) => void;
    setChatMessageRating: (chatMessageRating: Record<string, AssistantChatLogRating>) => void;
    setChatMessageRatingById: (chatID: string, rating: AssistantChatLogRating) => void;
    setSelectedArchiveChatId: (chatID: string) => void;
    setSelectedMenuType: (menuType: AssistantChatMenuType) => void;
    setChatMessageSearchResult: (searchResult: AssistantChatMessageSearchResult, message: AssistantChatMessage) => void;
}

const assistantChatDataStore = createStore<AssistantChatDataStore>((set) => ({
    ...assistantChatMessageInitialState,
    setAssistantChatDataStore: (assistantChatDataProps: AssistantChatDataProps) =>
        set((state) => ({ ...state, ...assistantChatDataProps })),
    setParentChatId: (parentChatID: string) => set((state) => ({ ...state, parentChatID })),
    setChatMessageRating: (chatMessageRating: Record<string, AssistantChatLogRating>) =>
        set((state) => ({
            ...state,
            chatMessageRating,
        })),
    setChatMessageRatingById: (chatID: string, rating: AssistantChatLogRating) => {
        set((state) => {
            const prevChatMessageRating = { ...(state.chatMessageRating || {}) };
            prevChatMessageRating[chatID] = rating;
            return {
                ...state,
                chatMessageRating: prevChatMessageRating,
            };
        });
    },
    setSelectedArchiveChatId: (selectedArchiveChatId: string) => set((state) => ({ ...state, selectedArchiveChatId })),
    setSelectedMenuType: (selectedMenuType: AssistantChatMenuType) => set((state) => ({ ...state, selectedMenuType })),
    setChatMessageSearchResult: (searchResult: AssistantChatMessageSearchResult, message: AssistantChatMessage) => {
        set((state) => {
            const prevChatMessageResult = { ...(state.chatMessageResult || {}) };
            prevChatMessageResult[message.createDate][message.id] = {
                ...prevChatMessageResult[message.createDate][message.id],
                assistantChatMessageSearchResult: searchResult,
            };

            return {
                ...state,
                chatMessageResult: { ...prevChatMessageResult },
                parentChatID: searchResult.parentChatID,
            };
        });
    },
}));

export const useAssistantChatStore = () => useStore(assistantChatDataStore);
